import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { lazy, Suspense } from "react";

const Toaster = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
const CookieConsentBanner = lazy(() =>
  import("@/components/CookieConsentBanner").then((m) => ({ default: m.CookieConsentBanner })),
);
const SiteFooter = lazy(() =>
  import("@/components/SiteFooter").then((m) => ({ default: m.SiteFooter })),
);
const SupportChatBubble = lazy(() =>
  import("@/components/SupportChatBubble").then((m) => ({ default: m.SupportChatBubble })),
);


import appCss from "../styles.css?url";


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "theme-color", content: "#0F172A" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "SmartEssay" },
      { title: "SmartEssay — The #1 All-in-One Essay Writing Toolkit for Students" },
      { name: "description", content: "SmartEssay is the all-in-one essay writing toolkit for students: outline generator, sample essay writer with real MLA & APA citations, guided AI tutor, essay reviewer, grammar checker, and PDF export — one login, every stage of the essay." },
      { name: "author", content: "SmartEssay" },
      { name: "keywords", content: "all-in-one essay writing toolkit, essay writer, essay generator, essay outline generator, essay reviewer, AI essay tutor, essay checker, MLA APA citation generator, essay help for students" },
      { property: "og:site_name", content: "SmartEssay" },
      { property: "og:title", content: "SmartEssay — The #1 All-in-One Essay Writing Toolkit for Students" },
      { property: "og:description", content: "The Ultimate writing assistant. Outlines, sample essays with real citations, a guided AI tutor, and an essay reviewer. Free to try." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://smartessay.ai" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow" },
      { name: "msvalidate.01", content: "25EC3D9130524B6C7463311EDE47CF0B" },
      { name: "google-site-verification", content: "TFIVcw6V7e1nylB0j5qtnYO5_HT4eYlivYX1lf-O5uI" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap",
      },
      { rel: "alternate", hrefLang: "en", href: "https://smartessay.ai" },
      { rel: "alternate", hrefLang: "en-us", href: "https://smartessay.ai" },
      { rel: "alternate", hrefLang: "en-gb", href: "https://smartessay.ai" },
      { rel: "alternate", hrefLang: "en-au", href: "https://smartessay.ai" },
      { rel: "alternate", hrefLang: "en-ca", href: "https://smartessay.ai" },
      { rel: "alternate", hrefLang: "en-in", href: "https://smartessay.ai" },
      { rel: "alternate", hrefLang: "x-default", href: "https://smartessay.ai" },
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Tilt University",
        url: "https://tilt.university",
        email: "support@tilt.university",
        brand: {
          "@type": "Brand",
          name: "SmartEssay",
          url: "https://smartessay.ai",
        },
        sameAs: [
          "https://www.youtube.com/@Tilt.University",
          "https://www.instagram.com/tiltuni",
          // TODO: add TikTok once handle is fixed (June 29)
        ],
      }),
    }],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Suspense fallback={null}>
        <Toaster richColors position="top-center" />
        <CookieConsentBanner />
        <SiteFooter />
        <SupportChatBubble />
      </Suspense>
    </QueryClientProvider>
  );


}
